import {
  CloseOutlined,
  EditFilled,
  EllipsisOutlined,
  FileTextOutlined,
  FilterOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Dropdown, Form, Input, message, Row, Select, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import ErrorResult from '../components/ErrorResult';
import Filter from '../components/Filter';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import PermissionChecker from '../components/PermissionChecker';
import RequestJobPostingActionsModal, {
  RequestJobPostingActions,
} from '../components/RequestJobPostingModal/RequestJobPostingActionsModal';
import RequestJobPostingTable from '../components/RequestJobPostingTable';
import SearchParam from '../components/SearchParam';
import usePermissionCheckerQuery from '../hooks/usePermissionCheckerQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { withRouteChange } from '../services/APM';
import { RequestJobPostingCollection, RequestJobPostingDetail } from '../types';
import { useVixtingParams } from '../utils';

const STALE_TIME = 1000 * 60 * 5;

export interface RequestJobPostingActionProps {
  requestJobPosting: RequestJobPostingCollection | RequestJobPostingDetail;
  action: RequestJobPostingActions;
  open?: boolean;
}

type TableActionsProps = {
  record: RequestJobPostingCollection | RequestJobPostingDetail;
  onViewClick: () => void;
  onEditClick: () => void;
  onCancelClick: () => void;
};

function TableActions({ record, onViewClick, onEditClick, onCancelClick }: TableActionsProps) {
  const hasCreateAndEditRequestJobPostingPermission = usePermissionCheckerQuery([
    'create_and_edit_request_job_postings',
  ]);

  return (
    <Dropdown
      menu={{
        onClick: ({ key }) => {
          switch (key) {
            case 'view':
              onViewClick();
              break;
            case 'edit':
              onEditClick();
              break;
            case 'cancel':
              onCancelClick();
              break;
          }
        },
        items: [
          { key: 'view', icon: <FileTextOutlined />, label: 'Analisar requisição' },
          {
            key: 'edit',
            disabled:
              !hasCreateAndEditRequestJobPostingPermission.data ||
              ['CANCELED', 'REJECTED', 'APPROVED'].includes(record.status),
            icon: <EditFilled />,
            label: (
              <Tooltip
                title={
                  !hasCreateAndEditRequestJobPostingPermission.data
                    ? 'Você não tem permissão.'
                    : ['CANCELED', 'REJECTED', 'APPROVED'].includes(record.status)
                    ? 'Essa vaga não pode ser editada, pois já foi aprovada, reprovada ou cancelada.'
                    : ''
                }
              >
                Editar
              </Tooltip>
            ),
          },
          {
            key: 'cancel',
            disabled:
              !hasCreateAndEditRequestJobPostingPermission.data ||
              ['CANCELED', 'REJECTED', 'APPROVED'].includes(record.status),
            icon: <CloseOutlined />,
            label: (
              <Tooltip
                title={
                  !hasCreateAndEditRequestJobPostingPermission.data
                    ? 'Você não tem permissão.'
                    : ['CANCELED', 'REJECTED', 'APPROVED'].includes(record.status)
                    ? 'Essa vaga não pode ser cancelada, pois já foi aprovada, reprovada ou cancelada.'
                    : ''
                }
              >
                Cancelar
              </Tooltip>
            ),
          },
        ],
      }}
    >
      <Button type="text" icon={<EllipsisOutlined />} aria-label={`Ações para ${record.name}`} />
    </Dropdown>
  );
}

const RequestJobPostings = () => {
  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();
  const [searchParams, setSearchParams] = useSearchParams({ status: '' });
  const [selected, setSelected] = useState<RequestJobPostingActionProps>();

  useEffect(() => {
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const vixtingParams = useVixtingParams(searchParams, 'vixtingrequestjobposting');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => {
    return vixtingParams.hasParams;
  });

  const requestJobPostings = useAPIQuery([
    '/request-job-postings/',
    {
      params: {
        search: searchParams.get('search') || undefined,
        status: (searchParams.get('status') as RequestJobPostingCollection['status']) || undefined,
        ...vixtingParams.apiParams,
        page: parseInt(searchParams.get('page') || '1', 10),
        page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        expand: 'approved_stages_count,stages_count',
      },
    },
  ]);

  const pagination = useMemo<React.ComponentProps<typeof RequestJobPostingTable>['pagination']>(
    () => ({
      showTotal: (total) => {
        return `Total: ${total} vagas`;
      },
      total: requestJobPostings.data?.count,
      onChange: (page, pageSize) => {
        searchParams.set('page', page.toString());
        searchParams.set('pageSize', pageSize.toString());
        setSearchParams(searchParams);
      },
      current: parseInt(searchParams.get('page') || '1', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
      pageSizeOptions: [10, 20],
    }),
    [requestJobPostings.data?.count, searchParams, setSearchParams]
  );

  const actionsRender = useCallback<
    React.ComponentProps<typeof RequestJobPostingTable>['actionsRender']
  >(
    (_value, record) => (
      <TableActions
        record={record}
        onViewClick={() =>
          navigate({
            pathname: '/:tenant/request-job-postings/:id/',
            params: { tenant, id: record.id },
          })
        }
        onEditClick={() =>
          navigate({
            pathname: '/:tenant/request-job-postings/:id/change/',
            params: { tenant, id: record.id },
          })
        }
        onCancelClick={() =>
          setSelected({
            requestJobPosting: record,
            action: 'CANCELED',
            open: true,
          })
        }
      />
    ),
    [navigate, tenant]
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar empresas da vixting!');
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar filiais da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar unidades de negócio da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar cargos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: vixtingParams.params.company,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!vixtingParams.params.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar departamentos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingAllDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const vixtingColSpan = config.data?.enableVixtingUnitBusiness ? 5 : 6;

  return (
    <Page>
      {selected?.requestJobPosting && (
        <RequestJobPostingActionsModal
          {...selected}
          onOk={() => {
            setSelected({ ...selected, open: false });
            requestJobPostings.refetch();
          }}
          onCancel={() => setSelected({ ...selected, open: false })}
        />
      )}
      <PageTitle
        title="Requisições"
        extra={[
          <PermissionChecker
            key="newRequestJobPosting"
            requiredPermissions={['create_and_edit_request_job_postings']}
          >
            <Link to="new">
              <Button icon={<PlusOutlined />}>Nova requisição</Button>
            </Link>
          </PermissionChecker>,
        ]}
      />
      <ErrorResult error={requestJobPostings.error}>
        <PageContent>
          <Card>
            <Filter>
              <Col span={24}>
                <Form layout="vertical">
                  <Row gutter={[12, 24]} align="middle">
                    <Col flex={1}>
                      <Form.Item label="Buscar">
                        <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
                          <Input.Search allowClear placeholder="Nome ou código da requisição" />
                        </SearchParam>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item label="Status">
                        <SearchParam
                          name="status"
                          defaultValue=""
                          onSetSearchParams={{ page: '1' }}
                        >
                          <Select
                            allowClear
                            options={[
                              { value: 'IN_PROGRESS', label: 'Em andamento' },
                              { value: 'NOT_INITIATED', label: 'Não iniciadas' },
                              { value: 'REJECTED', label: 'Rejeitadas' },
                              { value: 'APPROVED', label: 'Aprovadas' },
                              { value: 'CANCELED', label: 'Canceladas' },
                              { value: '', label: 'Todas' },
                            ]}
                          />
                        </SearchParam>
                      </Form.Item>
                    </Col>
                    <Col>
                      {config.data?.enableVixtingAdmission && (
                        <Form.Item label>
                          <Button
                            type={
                              showAdvancedFilters || vixtingParams.hasParams ? 'primary' : 'default'
                            }
                            ghost={showAdvancedFilters || vixtingParams.hasParams}
                            icon={<FilterOutlined />}
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                          >
                            Filtros avançados
                          </Button>
                        </Form.Item>
                      )}
                    </Col>
                    <Col>
                      <Form.Item label>
                        <Button type="default" onClick={() => setSearchParams({ page: '1' })}>
                          Limpar Filtros
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                  {config.data?.enableVixtingAdmission && showAdvancedFilters && (
                    <>
                      <Row gutter={[8, 8]}>
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Empresa">
                            <SearchParam
                              name={vixtingParams.queryParams.COMPANY}
                              onSetSearchParams={{
                                page: '1',
                                [vixtingParams.queryParams.JOB_ROLE]: '',
                                [vixtingParams.queryParams.DEPARTMENT]: '',
                                [vixtingParams.queryParams.AFFILIATE]: '',
                                [vixtingParams.queryParams.UNIT_BUSINESS]: '',
                              }}
                            >
                              <Select
                                showSearch
                                allowClear
                                placeholder="Empresa"
                                loading={vixtingCompaniesPages.isLoading}
                                data-testid="vixting-company-select"
                                options={vixtingCompanies.map((company) => ({
                                  key: company.id,
                                  label: company.name,
                                  value: company.id,
                                  'data-testid': `company-${company.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Filial">
                            <SearchParam
                              name={vixtingParams.queryParams.AFFILIATE}
                              onSetSearchParams={{ page: '1' }}
                            >
                              <Select
                                allowClear
                                showSearch
                                loading={vixtingAffiliatesPages.isLoading}
                                disabled={
                                  vixtingAffiliatesPages.isLoading || !vixtingParams.params.company
                                }
                                placeholder="Filial"
                                data-testid="vixting-affiliate-select"
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                }
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vixtingAffiliates.map((affiliate) => ({
                                  key: affiliate.id,
                                  label: affiliate.fantasyName,
                                  value: affiliate.id,
                                  'data-testid': `affiliate-${affiliate.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                        {config.data?.enableVixtingUnitBusiness && (
                          <Col span={4}>
                            <Form.Item label="Unidade de Negócio">
                              <SearchParam
                                name={vixtingParams.queryParams.UNIT_BUSINESS}
                                onSetSearchParams={{ page: '1' }}
                              >
                                <Select
                                  allowClear
                                  showSearch
                                  loading={vixtingUnitBusinessesPages.isLoading}
                                  disabled={
                                    vixtingUnitBusinessesPages.isLoading ||
                                    !vixtingParams.params.company
                                  }
                                  placeholder="Unidade de Negócio"
                                  data-testid="vixting-unit-business-select"
                                  filterSort={(optionA, optionB) =>
                                    (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                  }
                                  filterOption={(input, option) =>
                                    (option?.label ?? '')
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  options={vixtingUnitBusinesses.map((unitBusiness) => ({
                                    key: unitBusiness.id,
                                    label: unitBusiness.name,
                                    value: unitBusiness.id.toString(),
                                    'data-testid': `unit-business-${unitBusiness.id}`,
                                  }))}
                                />
                              </SearchParam>
                            </Form.Item>
                          </Col>
                        )}
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Cargo">
                            <SearchParam
                              name={vixtingParams.queryParams.JOB_ROLE}
                              onSetSearchParams={{ page: '1' }}
                            >
                              <Select
                                showSearch
                                allowClear
                                loading={vixtingJobRolesPages.isLoading}
                                disabled={
                                  vixtingJobRolesPages.isLoading || !vixtingParams.params.company
                                }
                                placeholder="Cargo"
                                data-testid="vixting-job-role-select"
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                }
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vixtingJobRoles.map((jobRole) => ({
                                  key: jobRole.id,
                                  label: jobRole.name,
                                  value: jobRole.id.toString(),
                                  'data-testid': `jobRole-${jobRole.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                        <Col span={vixtingColSpan}>
                          <Form.Item label="Departamento">
                            <SearchParam
                              name={vixtingParams.queryParams.DEPARTMENT}
                              onSetSearchParams={{ page: '1' }}
                            >
                              <Select
                                showSearch
                                allowClear
                                loading={vixtingDepartmentsPages.isLoading}
                                disabled={
                                  !vixtingParams.params.company || vixtingDepartmentsPages.isLoading
                                }
                                placeholder="Departamento"
                                data-testid="vixting-department-select"
                                filterSort={(optionA, optionB) =>
                                  (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                                }
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={vixtingAllDepartments.map((department) => ({
                                  key: department.id,
                                  label: department.name,
                                  value: department.id.toString(),
                                  'data-testid': `department-${department.id}`,
                                }))}
                              />
                            </SearchParam>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </Form>
              </Col>
            </Filter>
            <RequestJobPostingTable
              loading={requestJobPostings.isFetching}
              dataSource={requestJobPostings.data?.results}
              pagination={pagination}
              actionsRender={actionsRender}
            />
          </Card>
        </PageContent>
      </ErrorResult>
    </Page>
  );
};

export default withRouteChange()(RequestJobPostings);
